import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Table, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { CATALOG_WRITE_REFUSAL, refuseCatalogWrite } from '../AppShell/platformDirectory';

const { Title, Paragraph, Text } = Typography;

interface CatalogTypeRow {
  id?: string;
  subscription_code?: string;
  subscription_name?: string;
  base_price?: number;
  currency?: string;
  is_active?: boolean;
}

interface CatalogFeatureRow {
  id?: string;
  feature_code?: string;
  feature_name?: string;
  name?: string;
  is_active?: boolean;
}

/**
 * Read-only catalog. Create, update, and delete are refused here.
 * The subscription-types page still talks to the API directly and is not the Central write path.
 */
export const PlatformSubscriptionCatalogPage: React.FC = () => {
  const [types, setTypes] = useState<CatalogTypeRow[]>([]);
  const [features, setFeatures] = useState<CatalogFeatureRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<CatalogTypeRow[]>('/subscriptions/types', { params: { limit: 1000 } }),
      apiClient.get<CatalogFeatureRow[]>('/subscriptions/features', { params: { limit: 1000 } }),
    ])
      .then(([typeResponse, featureResponse]) => {
        if (cancelled) return;
        setTypes(Array.isArray(typeResponse.data) ? typeResponse.data : []);
        setFeatures(Array.isArray(featureResponse.data) ? featureResponse.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTypes([]);
        setFeatures([]);
        setError(getApiErrorMessage(err, 'Could not load the subscription catalog.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <Title level={3}>Subscription catalog</Title>
      <Paragraph>
        Types and features are listed from the API. {CATALOG_WRITE_REFUSAL} The prime template is Subscription Bundle
        (docs/templates/prime_artifacts/subscription_bundle.json). Stripe price ids are properties on that bundle, not
        columns on the subscription type row.
      </Paragraph>
      <Paragraph>
        <Link to="/subscription-management">Company plans</Link>
        {' · '}
        <Link to="/subscription-types-management">API type editor (not Central)</Link>
      </Paragraph>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      {writeError ? <Alert type="warning" showIcon message={writeError} style={{ marginBottom: 16 }} /> : null}
      {(types.length >= 1000 || features.length >= 1000) && !error ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The catalog list is capped at 1,000 types and 1,000 features."
        />
      ) : null}
      <Button
        type="default"
        style={{ marginBottom: 16 }}
        onClick={() => setWriteError(refuseCatalogWrite().message)}
      >
        Request a catalog change
      </Button>
      <Title level={4}>Types</Title>
      <Table
        rowKey={(row) => String(row.id || row.subscription_code)}
        loading={loading}
        dataSource={types}
        pagination={false}
        columns={[
          { title: 'Code', dataIndex: 'subscription_code', key: 'code' },
          { title: 'Name', dataIndex: 'subscription_name', key: 'name' },
          {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'active',
            render: (value?: boolean) => (value === false ? 'No' : 'Yes'),
          },
          {
            title: 'Price',
            key: 'price',
            render: (_, row) =>
              row.base_price !== undefined && row.base_price !== null
                ? `${row.base_price} ${row.currency || ''}`.trim()
                : '—',
          },
          {
            title: 'Stripe price',
            key: 'stripe',
            render: () => <Text type="secondary">Not on the type row</Text>,
          },
        ]}
      />
      <Title level={4} style={{ marginTop: 24 }}>Features</Title>
      <Table
        rowKey={(row) => String(row.id || row.feature_code || row.feature_name)}
        loading={loading}
        dataSource={features}
        pagination={false}
        columns={[
          { title: 'Code', dataIndex: 'feature_code', key: 'code', render: (value?: string) => value || '—' },
          {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'active',
            render: (value?: boolean) => (value === false ? 'No' : 'Yes'),
          },
          {
            title: 'Name',
            key: 'name',
            render: (_, row) => row.feature_name || row.name || '—',
          },
        ]}
      />
    </Card>
  );
};

export default PlatformSubscriptionCatalogPage;
